import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import PasswordRules from "components/common/PasswordRules";
import { Fragment, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useCelleraTheme } from "theme/useCelleraTheme";
import { ResetPasswordFormType } from "types";
import { buttonVariants } from "utils/muiTailwindStyles";

interface Props {
  isSubmitting: boolean;
  isValid: boolean;
}

function ResetPasswordForm({ isSubmitting, isValid }: Props) {
  const { colors } = useCelleraTheme();
  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmNewPassword: false,
  });

  const { control, watch } = useFormContext<ResetPasswordFormType>();

  return (
    <Fragment>
      <Box className="text-center space-y-6">
        <Typography
          variant="h5"
          component="h5"
          className="text-f22 leading-7 text-surface-dark"
        >
          Enter New Password
        </Typography>
        <Box>
          <Typography variant="body2" className="text-black text-base px-4">
            Please enter and confirm your new password to complete the reset
            process.
          </Typography>
        </Box>
      </Box>
      <Box className="space-y-6">
        <Controller
          name="newPassword"
          control={control}
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              id="new-password"
              fullWidth
              label="New Password"
              type={showPassword.newPassword ? "text" : "password"}
              error={!!error}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() =>
                          setShowPassword({
                            ...showPassword,
                            newPassword: !showPassword.newPassword,
                          })
                        }
                        edge="end"
                        sx={{ color: colors.text.secondary }}
                      >
                        {showPassword.newPassword ? (
                          <Visibility />
                        ) : (
                          <VisibilityOff />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
                htmlInput: {
                  maxLength: 255,
                },
              }}
            />
          )}
        />

        <Controller
          name="confirmNewPassword"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              id="confirm-new-password"
              fullWidth
              label="Confirm New Password"
              type={showPassword.confirmNewPassword ? "text" : "password"}
              error={!!error}
              helperText={error?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() =>
                          setShowPassword({
                            ...showPassword,
                            confirmNewPassword:
                              !showPassword.confirmNewPassword,
                          })
                        }
                        edge="end"
                        sx={{ color: colors.text.secondary }}
                      >
                        {showPassword.confirmNewPassword ? (
                          <Visibility />
                        ) : (
                          <VisibilityOff />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
                htmlInput: {
                  maxLength: 255,
                },
              }}
            />
          )}
        />

        <PasswordRules password={watch("newPassword")} />

        <Button
          type="submit"
          variant="contained"
          className={`${buttonVariants.primary.className} w-full text-base rounded-full py-3 disabled:text-surface-light disabled:opacity-40`}
          sx={{
            ...buttonVariants.primary.sx,
          }}
          disabled={!isValid}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Reset Password"
          )}
        </Button>
      </Box>
    </Fragment>
  );
}

export default ResetPasswordForm;
